<template>
  <ElDropdown
    :trigger="trigger"
    :effect="effect"
    :type="type"
    :placement="placement"
    :disabled="disabled"
    :split-button="splitButton"
    :hide-on-click="hideOnClick"
    :show-timeout="showTimeout"
    :hide-timeout="hideTimeout"
    :role="role"
    :tabindex="tabindex"
    :max-height="maxHeight"
    :popper-class="popperClass"
    :popper-options="popperOptions"
    :teleported="teleported"
    @click="handleClick"
    @command="handleCommand"
    @visible-change="handleVisibleChange"
  >
    <!-- 触发器插槽 -->
    <template #default>
      <slot>
        <ElButton
          :type="buttonType"
          :size="buttonSize"
          :disabled="disabled"
          :loading="loading"
        >
          {{ buttonText }}
          <ElIcon class="el-icon--right">
            <ArrowDown />
          </ElIcon>
        </ElButton>
      </slot>
    </template>

    <!-- 下拉菜单插槽 -->
    <template #dropdown>
      <ElDropdownMenu>
        <slot name="dropdown">
          <ElDropdownItem
            v-for="item in items"
            :key="item.key || item.label"
            :command="item.command || item.key"
            :disabled="item.disabled"
            :divided="item.divided"
            :icon="item.icon"
          >
            <ElIcon v-if="item.icon" class="dropdown-item-icon">
              <component :is="item.icon" />
            </ElIcon>
            {{ item.label }}
          </ElDropdownItem>
        </slot>
      </ElDropdownMenu>
    </template>
  </ElDropdown>
</template>

<script setup lang="ts">
/**
 * Dropdown 下拉菜单组件
 * 基于 Element Plus Dropdown 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ArrowDown } from '@element-plus/icons-vue'
import type { Placement } from '@element-plus/components/popper'
import type { ButtonType, ButtonSize } from 'element-plus'

// 下拉菜单项接口
interface DropdownItem {
  /** 唯一标识 */
  key?: string | number
  /** 显示文本 */
  label: string
  /** 命令值 */
  command?: string | number
  /** 是否禁用 */
  disabled?: boolean
  /** 是否显示分割线 */
  divided?: boolean
  /** 图标 */
  icon?: any
}

// 接口定义
interface Props {
  /** 触发方式 */
  trigger?: 'hover' | 'click' | 'contextmenu'
  /** 主题效果 */
  effect?: 'dark' | 'light'
  /** 菜单类型 */
  type?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** 菜单弹出位置 */
  placement?: Placement
  /** 是否禁用 */
  disabled?: boolean
  /** 是否使用分割按钮 */
  splitButton?: boolean
  /** 是否在点击菜单项后隐藏菜单 */
  hideOnClick?: boolean
  /** 展开下拉菜单的延时 */
  showTimeout?: number
  /** 收起下拉菜单的延时 */
  hideTimeout?: number
  /** ARIA role */
  role?: string
  /** tabindex */
  tabindex?: number
  /** 下拉菜单的最大高度 */
  maxHeight?: string | number
  /** 下拉菜单的类名 */
  popperClass?: string
  /** popper.js 的参数 */
  popperOptions?: any
  /** 是否使用 teleport */
  teleported?: boolean
  /** 按钮文本 */
  buttonText?: string
  /** 按钮类型 */
  buttonType?: ButtonType
  /** 按钮尺寸 */
  buttonSize?: ButtonSize
  /** 按钮加载状态 */
  loading?: boolean
  /** 下拉菜单项 */
  items?: DropdownItem[]
}

interface Emits {
  /** 点击事件 */
  click: []
  /** 点击菜单项触发的事件回调 */
  command: [command: string | number]
  /** 下拉框出现/隐藏时触发 */
  'visible-change': [visible: boolean]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  trigger: 'hover',
  effect: 'light',
  type: undefined,
  placement: 'bottom',
  disabled: false,
  splitButton: false,
  hideOnClick: true,
  showTimeout: 250,
  hideTimeout: 150,
  role: 'menu',
  tabindex: 0,
  maxHeight: '',
  popperClass: '',
  teleported: true,
  buttonText: '下拉菜单',
  buttonType: 'default',
  buttonSize: 'default',
  loading: false,
  items: () => []
})

// 定义 emits
const emit = defineEmits<Emits>()

/**
 * 点击事件处理
 */
const handleClick = (): void => {
  emit('click')
}

/**
 * 菜单项点击事件处理
 */
const handleCommand = (command: string | number): void => {
  emit('command', command)
}

/**
 * 可见性变化事件处理
 */
const handleVisibleChange = (visible: boolean): void => {
  emit('visible-change', visible)
}
</script>

<style scoped>
.dropdown-item-icon {
  margin-right: 8px;
  font-size: 16px;
}

/* 自定义样式变量 */
:deep(.el-dropdown) {
  display: inline-flex;
  align-items: center;
}

:deep(.el-dropdown__caret-button) {
  border-left: 1px solid var(--el-border-color);
}

:deep(.el-dropdown-menu) {
  --el-dropdown-menu-border-radius: 6px;
  border-radius: var(--el-dropdown-menu-border-radius);
  box-shadow: var(--el-box-shadow-light);
}

:deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 1.5;
  transition: all 0.2s;
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-primary);
}

:deep(.el-dropdown-menu__item.is-disabled) {
  color: var(--el-text-color-disabled);
  cursor: not-allowed;
}

:deep(.el-dropdown-menu__item.is-disabled:hover) {
  background-color: transparent;
}

/* 分割线样式 */
:deep(.el-dropdown-menu__item--divided) {
  border-top: 1px solid var(--el-border-color-lighter);
  margin-top: 6px;
  padding-top: 14px;
}

/* 不同主题效果 */
:deep(.el-dropdown-menu.el-popper[data-popper-placement]) {
  margin-top: 8px;
}

/* 暗色主题 */
:deep(.el-dropdown-menu.el-dropdown-menu--dark) {
  background-color: var(--el-bg-color-overlay);
  border: 1px solid var(--el-border-color-darker);
}

:deep(.el-dropdown-menu.el-dropdown-menu--dark .el-dropdown-menu__item) {
  color: var(--el-text-color-primary);
}

:deep(.el-dropdown-menu.el-dropdown-menu--dark .el-dropdown-menu__item:hover) {
  background-color: var(--el-fill-color-dark);
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-dropdown-menu) {
    min-width: 120px;
    max-width: 200px;
  }

  :deep(.el-dropdown-menu__item) {
    padding: 10px 12px;
    font-size: 16px;
  }
}

/* 动画效果 */
:deep(.el-dropdown-menu) {
  animation: dropdown-fade-in 0.2s ease-out;
}

@keyframes dropdown-fade-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
